import {
  actionFlags as configActionFlags,
  featureFlags as configFeatureFlags,
} from "../config/featureConfig";
import {
  createDefaultPermissionProfile,
  defaultActionFlags,
  defaultFeatureFlags,
} from "../config/permissionDefaults";

export const DEFAULT_PROFILE_KEY = "default";

const mergeBooleanMap = (base = {}, override = {}) => {
  const merged = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    if (typeof value === "boolean") {
      merged[key] = value;
    }
  });
  return merged;
};

const mergeActionFlags = (base = {}, override = {}) => {
  const merged = Object.fromEntries(
    Object.entries(base).map(([group, actions]) => [group, { ...actions }])
  );

  Object.entries(override).forEach(([group, actions]) => {
    if (!actions || typeof actions !== "object") {
      return;
    }
    merged[group] = mergeBooleanMap(merged[group] ?? {}, actions);
  });

  return merged;
};

const applyConfigCeiling = (profile) => ({
  featureFlags: Object.fromEntries(
    Object.entries(configFeatureFlags).map(([key, enabled]) => [
      key,
      enabled !== false && profile.featureFlags?.[key] !== false,
    ])
  ),
  actionFlags: Object.fromEntries(
    Object.entries(configActionFlags).map(([group, actions]) => [
      group,
      Object.fromEntries(
        Object.entries(actions).map(([action, enabled]) => [
          action,
          enabled !== false && profile.actionFlags?.[group]?.[action] !== false,
        ])
      ),
    ])
  ),
});

const hasConfiguredKeys = (profile) => {
  if (!profile || typeof profile !== "object") {
    return false;
  }

  const hasFeatureFlags = Object.keys(profile.featureFlags ?? {}).length > 0;
  const hasActionFlags = Object.values(profile.actionFlags ?? {}).some(
    (actions) => actions && Object.keys(actions).length > 0
  );

  return hasFeatureFlags || hasActionFlags;
};

export const normalizePermissionEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

/** Migrate legacy tableActions key to actionFlags. */
export const normalizeRemoteProfile = (profile) => {
  if (!profile || typeof profile !== "object") {
    return profile;
  }
  const actionFlags = profile.actionFlags ?? profile.tableActions ?? {};
  const { tableActions: _legacy, ...rest } = profile;
  return { ...rest, actionFlags };
};

export const normalizePermissionsRegistry = (registry) => {
  if (!registry || typeof registry !== "object" || Array.isArray(registry)) {
    return {};
  }

  return Object.entries(registry).reduce((acc, [key, profile]) => {
    if (key === DEFAULT_PROFILE_KEY) {
      acc[DEFAULT_PROFILE_KEY] = normalizeRemoteProfile(profile);
      return acc;
    }

    const normalizedEmail = normalizePermissionEmail(key);
    if (!normalizedEmail) {
      return acc;
    }
    acc[normalizedEmail] = normalizeRemoteProfile(profile);
    return acc;
  }, {});
};

const mergeRemoteProfile = (remoteProfile) => {
  if (!hasConfiguredKeys(remoteProfile)) {
    return createDefaultPermissionProfile();
  }

  const baseProfile = createDefaultPermissionProfile();
  return {
    featureFlags: mergeBooleanMap(baseProfile.featureFlags, remoteProfile.featureFlags),
    actionFlags: mergeActionFlags(baseProfile.actionFlags, remoteProfile.actionFlags),
  };
};

export function resolveUserPermissions(userEmail, remoteRegistry) {
  const normalizedEmail = normalizePermissionEmail(userEmail);

  if (!normalizedEmail) {
    return applyConfigCeiling(createDefaultPermissionProfile());
  }

  const remoteProfile =
    remoteRegistry?.[normalizedEmail] ?? remoteRegistry?.[DEFAULT_PROFILE_KEY];
  let mergedProfile;

  if (!remoteProfile) {
    mergedProfile = createDefaultPermissionProfile();
  } else if (!hasConfiguredKeys(remoteProfile)) {
    mergedProfile = createDefaultPermissionProfile();
  } else {
    mergedProfile = mergeRemoteProfile(normalizeRemoteProfile(remoteProfile));
  }

  return applyConfigCeiling(mergedProfile);
}

export function isActionAllowed(actionFlags, group, action) {
  return actionFlags?.[group]?.[action] !== false;
}

export function resolveTicketPermissions(actionFlags) {
  const canAssignToOthers = isActionAllowed(actionFlags, "gestioneTickets", "assegnaAdAltri");
  const canSelfAssign = isActionAllowed(actionFlags, "gestioneTickets", "autoAssegnazione");

  let visibilityScope = "ownOnly";
  if (canAssignToOthers) {
    visibilityScope = "all";
  } else if (canSelfAssign) {
    visibilityScope = "unassignedAndOwn";
  }

  return {
    canSelfAssign,
    canAssignToOthers,
    visibilityScope,
  };
}

const DEFAULT_ROUTE_CANDIDATES = [
  { path: "/dashboard", flag: "enableDashboard" },
  { path: "/gestione-ecommerce", flag: "enableGestioneEcommerce" },
  { path: "/gestione-prodotti", flag: "enableGestioneProdotti" },
  { path: "/gestione-categorie", flag: "enableGestioneCategorie" },
  { path: "/gestione-utenti", flag: "enableGestioneUtenti" },
  { path: "/gestione-tickets", flag: "enableGestioneTickets" },
  { path: "/newsletter", flag: "enableNewsletter" },
  { path: "/gestione-contenuti/banner", flag: "enableGestioneBanners" },
  { path: "/impostazioni", flag: "enableImpostazioni" },
  { path: "/manutenzione-cataloghi", flag: "enableManutenzioneCataloghi" },
  { path: "/sincronizzazione", flag: "enableSincronizzazione" },
];

export function getDefaultAdminRoute(featureFlags = {}) {
  for (const candidate of DEFAULT_ROUTE_CANDIDATES) {
    if (featureFlags[candidate.flag]) {
      return candidate.path;
    }
  }
  return null;
}

export const PERMISSION_SCHEMA = {
  featureFlags: defaultFeatureFlags,
  actionFlags: defaultActionFlags,
};

/** Persistable profile shape (actionFlags only, no legacy key). */
export function toPersistedProfile(profile) {
  const normalized = normalizeRemoteProfile(profile);
  return {
    featureFlags: { ...(normalized.featureFlags ?? {}) },
    actionFlags: { ...(normalized.actionFlags ?? {}) },
  };
}
